/*
 * Diffusion Denoising: teaching simulator (Case Study, SL + HL)
 * ------------------------------------------------------------------------------
 * WHAT IT TEACHES
 *   A diffusion model turns random noise into an image by removing a little
 *   noise at each step, over many steps.
 *     • It starts from pure random noise, not from a photograph.
 *     • A neural denoiser, typically a convolutional neural network (CNN),
 *       removes a little noise at each step (labelled only, not modelled).
 *     • More steps give a cleaner image but cost more compute.
 *     • The training data decides what the image becomes: change the target
 *       picture and something different emerges.
 *
 * This is an ILLUSTRATION, not real machine learning. Nothing is trained, no
 * dataset is loaded, no library is used. "Denoising" is faked as a blend from a
 * fixed random-noise field toward a chosen target picture, with the added noise
 * reduced a little each step. It only needs to look like progressive denoising.
 *
 * CONTROLS
 *   • Target picture (select) ....... fox / landscape / city skyline: the
 *                                     "training data" that decides the output.
 *   • Number of steps (slider 4–50) . how many denoising steps a full run takes;
 *                                     fewer steps leave a rougher image.
 *   • Denoise step (button) ......... advance one step (remove a little noise).
 *   • Play / Pause (button) ......... auto-run the steps (disabled when the
 *                                     browser requests reduced motion; step
 *                                     manually instead).
 *   • New noise (button) ............ reset to a fresh field of random noise.
 *   • Readouts ...................... step counter (t of N) and a compute-cost
 *                                     indicator that rises as steps are taken.
 *   • Explainer accordion ........... how a diffusion model works.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

// ── brookebit palette (shared with the other simulators) ───────────────────────
const B = {
  bg:           '#faf7f2',
  surface:      '#ffffff',
  border:       'rgba(60,45,30,0.10)',
  borderStrong: 'rgba(60,45,30,0.16)',
  text:         '#2a241d',
  muted:        '#6b6055',
  faint:        '#9a8f82',
  green:        '#7BC19E',
  greenDark:    '#5fa882',
  greenSoft:    '#eef6f1',
  charcoal:     '#3A3D3C',
  blue:         '#5a7abf',
  blueDark:     '#3d5c9e',
  amber:        '#f0c74e',
  amberDark:    '#c9a12e',
};

const font = "'Nunito', -apple-system, sans-serif";
const mono = "'JetBrains Mono', 'Fira Mono', monospace";

// Internal image resolution. The canvas is scaled up to fill its column, so the
// noise stays fine-grained while the block sampling (below) keeps low-step runs
// visibly rough.
const IW = 240;
const IH = 180;

// ── Target pictures ────────────────────────────────────────────────────────────
// Each draws a simple, recognisable scene onto an offscreen IW×IH canvas. These
// stand in for "what the model was trained on": change the target and a
// different image emerges from the same noise.

function drawFox(ctx) {
  // Sky and ground
  const sky = ctx.createLinearGradient(0, 0, 0, IH);
  sky.addColorStop(0, '#eaf1f6');
  sky.addColorStop(1, '#dfeae2');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, IW, IH);
  ctx.fillStyle = '#cfe3d3';
  ctx.fillRect(0, IH * 0.78, IW, IH * 0.22);

  const cx = IW / 2;
  // Ears
  ctx.fillStyle = '#e07a33';
  ctx.beginPath(); ctx.moveTo(cx - 58, 44); ctx.lineTo(cx - 30, 96); ctx.lineTo(cx - 78, 92); ctx.closePath(); ctx.fill();
  ctx.beginPath(); ctx.moveTo(cx + 58, 44); ctx.lineTo(cx + 30, 96); ctx.lineTo(cx + 78, 92); ctx.closePath(); ctx.fill();
  ctx.fillStyle = '#8a3f18';
  ctx.beginPath(); ctx.moveTo(cx - 56, 56); ctx.lineTo(cx - 40, 90); ctx.lineTo(cx - 66, 88); ctx.closePath(); ctx.fill();
  ctx.beginPath(); ctx.moveTo(cx + 56, 56); ctx.lineTo(cx + 40, 90); ctx.lineTo(cx + 66, 88); ctx.closePath(); ctx.fill();

  // Head (orange rounded top, tapering to a chin)
  ctx.fillStyle = '#e8823c';
  ctx.beginPath();
  ctx.moveTo(cx - 62, 92);
  ctx.quadraticCurveTo(cx - 74, 118, cx - 40, 128);
  ctx.lineTo(cx, 168);
  ctx.lineTo(cx + 40, 128);
  ctx.quadraticCurveTo(cx + 74, 118, cx + 62, 92);
  ctx.quadraticCurveTo(cx, 66, cx - 62, 92);
  ctx.closePath();
  ctx.fill();

  // White cheeks / muzzle
  ctx.fillStyle = '#fbf6ef';
  ctx.beginPath();
  ctx.moveTo(cx - 34, 116);
  ctx.lineTo(cx, 168);
  ctx.lineTo(cx + 34, 116);
  ctx.quadraticCurveTo(cx, 138, cx - 34, 116);
  ctx.closePath();
  ctx.fill();

  // Eyes
  ctx.fillStyle = '#2a241d';
  ctx.beginPath(); ctx.ellipse(cx - 26, 112, 7, 9, 0, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.ellipse(cx + 26, 112, 7, 9, 0, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#fff';
  ctx.beginPath(); ctx.arc(cx - 24, 109, 2, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(cx + 28, 109, 2, 0, Math.PI * 2); ctx.fill();

  // Nose
  ctx.fillStyle = '#2a241d';
  ctx.beginPath(); ctx.moveTo(cx - 8, 146); ctx.lineTo(cx + 8, 146); ctx.lineTo(cx, 156); ctx.closePath(); ctx.fill();
}

function drawLandscape(ctx) {
  // Sky
  const sky = ctx.createLinearGradient(0, 0, 0, IH);
  sky.addColorStop(0, '#8fc4e8');
  sky.addColorStop(1, '#d9eef7');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, IW, IH);
  // Sun
  ctx.fillStyle = '#f6d268';
  ctx.beginPath(); ctx.arc(IW - 58, 46, 24, 0, Math.PI * 2); ctx.fill();
  // Far hill
  ctx.fillStyle = '#7fae7a';
  ctx.beginPath();
  ctx.moveTo(0, 118);
  ctx.quadraticCurveTo(IW * 0.35, 74, IW * 0.7, 116);
  ctx.quadraticCurveTo(IW * 0.88, 138, IW, 110);
  ctx.lineTo(IW, IH); ctx.lineTo(0, IH); ctx.closePath(); ctx.fill();
  // Near hill
  ctx.fillStyle = '#5d9457';
  ctx.beginPath();
  ctx.moveTo(0, 140);
  ctx.quadraticCurveTo(IW * 0.4, 108, IW * 0.75, 142);
  ctx.quadraticCurveTo(IW * 0.9, 158, IW, 140);
  ctx.lineTo(IW, IH); ctx.lineTo(0, IH); ctx.closePath(); ctx.fill();
  // Lake
  ctx.fillStyle = '#6fa8cf';
  ctx.beginPath();
  ctx.moveTo(0, 164);
  ctx.quadraticCurveTo(IW * 0.5, 156, IW, 166);
  ctx.lineTo(IW, IH); ctx.lineTo(0, IH); ctx.closePath(); ctx.fill();
}

function drawSkyline(ctx) {
  // Dusk sky
  const sky = ctx.createLinearGradient(0, 0, 0, IH);
  sky.addColorStop(0, '#3b3b6b');
  sky.addColorStop(0.55, '#c76a5a');
  sky.addColorStop(1, '#f0b26b');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, IW, IH);
  // Low sun
  ctx.fillStyle = '#ffe0a3';
  ctx.beginPath(); ctx.arc(IW * 0.5, 128, 26, 0, Math.PI * 2); ctx.fill();

  // Building silhouettes with lit windows
  const buildings = [
    [8, 96], [40, 70], [70, 116], [96, 58], [128, 88], [158, 44], [190, 100], [216, 74],
  ];
  ctx.fillStyle = '#2c2b45';
  buildings.forEach(([x, top]) => {
    const w = 26;
    ctx.fillRect(x, top, w, IH - top);
  });
  // Windows
  ctx.fillStyle = 'rgba(246,214,104,0.9)';
  buildings.forEach(([x, top]) => {
    for (let wy = top + 8; wy < IH - 8; wy += 12) {
      for (let wx = x + 5; wx < x + 22; wx += 9) {
        if ((wx + wy) % 3 !== 0) ctx.fillRect(wx, wy, 4, 5);
      }
    }
  });
}

const TARGETS = {
  fox:       { label: 'Fox',          draw: drawFox },
  landscape: { label: 'Landscape',    draw: drawLandscape },
  skyline:   { label: 'City skyline', draw: drawSkyline },
};
const TARGET_ORDER = ['fox', 'landscape', 'skyline'];

// Fewer steps → coarser block sampling of the target → a visibly rougher image.
// 4 steps → 8-pixel blocks; 50 steps → 1 pixel (sharp).
function detailBlock(numSteps) {
  return Math.max(1, Math.round(1 + ((50 - numSteps) / 46) * 7));
}

// ── Explainer schematic: noise thinning out into a picture ─────────────────────
// Deterministic pseudo-random dots so it renders identically every time and
// never depends on colour alone (dot density carries the meaning).
function NoiseStrip() {
  const seeded = (i) => {
    const x = Math.sin(i * 12.9898) * 43758.5453;
    return x - Math.floor(x);
  };
  const panel = (keep, label, sub, key) => {
    const dots = [];
    for (let i = 0; i < 120; i++) {
      if (seeded(i * 2.3 + key * 50) < keep) {
        dots.push(
          <rect key={i}
            x={(seeded(i + key) * 92 + 2).toFixed(1)}
            y={(seeded(i * 1.7 + key) * 60 + 2).toFixed(1)}
            width="2.4" height="2.4" rx="0.6" fill={B.charcoal} opacity="0.85" />
        );
      }
    }
    return (
      <div key={key} style={{ flex: '1 1 130px', minWidth: 120 }}>
        <svg viewBox="0 0 96 64" style={{
          width: '100%', height: 'auto', display: 'block',
          borderRadius: 8, background: key === 2 ? B.greenSoft : B.bg,
          border: `1px solid ${B.border}`,
        }}>
          {/* faint "image" appears as noise clears */}
          <circle cx="48" cy="30" r={10 + (1 - keep) * 8} fill={B.green}
            opacity={((1 - keep) * 0.8).toFixed(2)} />
          {dots}
        </svg>
        <div style={{ fontSize: '0.74rem', fontWeight: 700, color: B.text, marginTop: 6 }}>{label}</div>
        <div style={{ fontSize: '0.7rem', color: B.faint, lineHeight: 1.45 }}>{sub}</div>
      </div>
    );
  };
  return (
    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.6rem', alignItems: 'flex-start' }}>
      {panel(0.95, 'Start: pure noise', 'Random noise injection. No picture yet.', 0)}
      {panel(0.45, 'Denoising', 'Each step removes a little noise.', 1)}
      {panel(0.08, 'Many steps later', 'A clean image the training data implies.', 2)}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function DiffusionDenoisingSimulator({ onBack }) {
  const canvasRef = useRef(null);

  const [target,   setTarget]   = useState('fox');
  const [numSteps, setNumSteps] = useState(20);
  const [step,     setStep]     = useState(0);   // steps taken (0 = pure noise)
  const [cost,     setCost]     = useState(0);   // cumulative denoiser passes this run
  const [playing,  setPlaying]  = useState(false);
  const [ready,    setReady]    = useState(false);
  const [hintOpen, setHintOpen] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  // Engine state mirrored in refs so the play loop and cost counter stay correct
  // regardless of React batching / StrictMode double-invocation.
  const noiseRef    = useRef(null);   // Uint8Array IW*IH*3
  const targetsRef  = useRef({});     // { key: Uint8ClampedArray (RGBA) }
  const stepRef     = useRef(0);
  const costRef     = useRef(0);
  const numStepsRef = useRef(20);

  // ── Detect reduced-motion preference ─────────────────────────────────────────
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduceMotion(mq.matches);
    apply();
    mq.addEventListener?.('change', apply);
    return () => mq.removeEventListener?.('change', apply);
  }, []);

  // ── Build the target pictures + first noise field once ───────────────────────
  const makeNoise = useCallback(() => {
    const arr = new Uint8Array(IW * IH * 3);
    for (let i = 0; i < arr.length; i++) arr[i] = (Math.random() * 256) | 0;
    noiseRef.current = arr;
  }, []);

  useEffect(() => {
    const off = document.createElement('canvas');
    off.width = IW; off.height = IH;
    const octx = off.getContext('2d');
    const store = {};
    TARGET_ORDER.forEach(key => {
      octx.clearRect(0, 0, IW, IH);
      TARGETS[key].draw(octx);
      store[key] = octx.getImageData(0, 0, IW, IH).data; // RGBA
    });
    targetsRef.current = store;
    makeNoise();
    setReady(true);
  }, [makeNoise]);

  // ── Render the current frame ─────────────────────────────────────────────────
  useEffect(() => {
    if (!ready) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = IW; canvas.height = IH;
    const ctx = canvas.getContext('2d');
    const img = ctx.createImageData(IW, IH);
    const data = img.data;
    const tgt = targetsRef.current[target];
    const noise = noiseRef.current;
    const p = numSteps > 0 ? step / numSteps : 0;     // 0 = pure noise, 1 = full image
    const B0 = detailBlock(numSteps);

    for (let y = 0; y < IH; y++) {
      for (let x = 0; x < IW; x++) {
        // Block-sample the target so low-step runs look coarse
        const sx = Math.min(IW - 1, Math.floor(x / B0) * B0 + (B0 >> 1));
        const sy = Math.min(IH - 1, Math.floor(y / B0) * B0 + (B0 >> 1));
        const ti = (sy * IW + sx) * 4;
        const ni = (y * IW + x) * 3;
        const oi = (y * IW + x) * 4;
        // Blend noise → target; the noise fraction shrinks as steps are taken
        data[oi]     = tgt[ti]     * p + noise[ni]     * (1 - p);
        data[oi + 1] = tgt[ti + 1] * p + noise[ni + 1] * (1 - p);
        data[oi + 2] = tgt[ti + 2] * p + noise[ni + 2] * (1 - p);
        data[oi + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  }, [ready, target, numSteps, step]);

  // ── Step engine ──────────────────────────────────────────────────────────────
  const applyStep = useCallback(() => {
    if (stepRef.current >= numStepsRef.current) return false;
    stepRef.current += 1;
    costRef.current += 1;
    setStep(stepRef.current);
    setCost(costRef.current);
    return true;
  }, []);

  // Auto-run loop
  useEffect(() => {
    if (!playing) return undefined;
    if (stepRef.current >= numStepsRef.current) { setPlaying(false); return undefined; }
    const id = setInterval(() => {
      if (!applyStep()) { setPlaying(false); }
    }, 260);
    return () => clearInterval(id);
  }, [playing, applyStep]);

  // ── Control handlers ─────────────────────────────────────────────────────────
  const handleStep = useCallback(() => { applyStep(); }, [applyStep]);

  const handlePlayPause = useCallback(() => {
    if (reduceMotion) return;
    setPlaying(pl => {
      if (!pl && stepRef.current >= numStepsRef.current) return false; // already finished
      return !pl;
    });
  }, [reduceMotion]);

  const handleNewNoise = useCallback(() => {
    makeNoise();
    stepRef.current = 0;
    costRef.current = 0;
    setStep(0);
    setCost(0);
    setPlaying(false);
  }, [makeNoise]);

  const handleTargetChange = useCallback((next) => {
    setTarget(next);
    // keep the current denoising progress; only the emerging picture changes
  }, []);

  const handleStepsChange = useCallback((next) => {
    numStepsRef.current = next;
    setNumSteps(next);
    if (stepRef.current > next) { stepRef.current = next; setStep(next); }
    setPlaying(false);
  }, []);

  const done = step >= numSteps;

  // ── Styles (shared vocabulary with the other simulators) ─────────────────────
  const s = {
    wrap: { fontFamily: font, background: B.bg, minHeight: '100vh', WebkitFontSmoothing: 'antialiased' },
    topbar: {
      position: 'sticky', top: 0, zIndex: 100, background: B.surface,
      borderBottom: `1px solid ${B.border}`, display: 'flex', alignItems: 'center',
      padding: '8px 1.5rem', minHeight: 60, gap: '0.75rem', flexWrap: 'wrap',
    },
    backBtn: {
      background: 'none', border: 'none', cursor: 'pointer', color: B.muted,
      fontFamily: font, fontSize: '0.85rem', fontWeight: 600, display: 'flex',
      alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 6,
    },
    title: { fontSize: '1rem', fontWeight: 700, color: B.text, margin: 0 },
    badge: {
      fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em',
      background: B.greenSoft, color: B.greenDark, padding: '3px 8px', borderRadius: 6,
    },
    lvlBadge: {
      fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em',
      background: '#efe7fb', color: '#7a5bbf', padding: '3px 7px', borderRadius: 6,
    },
    body: { maxWidth: 760, margin: '0 auto', padding: '2rem 1.5rem 3rem' },
    card: {
      background: B.surface, border: `1px solid ${B.border}`, borderRadius: 14,
      padding: '1.25rem', marginBottom: '1rem',
    },
    sectionLabel: {
      fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em',
      color: B.faint, textTransform: 'uppercase', marginBottom: '0.6rem',
    },
    controlRow: { display: 'flex', gap: '1.4rem', flexWrap: 'wrap', alignItems: 'flex-end' },
    ctrlGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
    ctrlLabel: {
      fontSize: '0.72rem', fontWeight: 700, color: B.muted,
      textTransform: 'uppercase', letterSpacing: '0.04em',
    },
    select: {
      fontFamily: font, fontSize: '0.88rem', border: `1.5px solid ${B.borderStrong}`,
      borderRadius: 8, padding: '6px 10px', background: B.bg, color: B.text, cursor: 'pointer',
    },
    rangeHint: { fontSize: '0.72rem', color: B.faint },
    stat: { display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' },
    statNum: { fontSize: '1.35rem', fontWeight: 800, color: B.text, fontFamily: mono, lineHeight: 1 },
    statLabel: { fontSize: '0.66rem', fontWeight: 700, color: B.faint, textTransform: 'uppercase', letterSpacing: '0.04em' },
    canvas: {
      display: 'block', width: '100%', height: 'auto', borderRadius: 10,
      background: B.charcoal, aspectRatio: `${IW} / ${IH}`,
    },
    btnRow: { display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '0.9rem' },
    btnPrimary: {
      fontFamily: font, fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer',
      background: B.green, color: '#fff', border: 'none', borderRadius: 9,
      padding: '9px 16px', display: 'inline-flex', alignItems: 'center', gap: 7,
    },
    btnGhost: {
      fontFamily: font, fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer',
      background: B.bg, color: B.text, border: `1.5px solid ${B.borderStrong}`,
      borderRadius: 9, padding: '9px 16px', display: 'inline-flex', alignItems: 'center', gap: 7,
    },
    costTrack: {
      height: 10, borderRadius: 6, background: B.bg,
      border: `1px solid ${B.border}`, overflow: 'hidden', marginTop: 6,
    },
    expBtn: {
      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      background: 'none', border: 'none', cursor: 'pointer', fontFamily: font,
      fontSize: '0.9rem', fontWeight: 600, color: B.text, padding: '0.9rem 1.25rem', borderRadius: 14,
    },
    expBody: { padding: '0 1.25rem 1.25rem', borderTop: `1px solid ${B.border}` },
    expGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.9rem' },
    expCard: { padding: '0.9rem', borderRadius: 8, border: `1px solid ${B.border}` },
    expCardTitle: { fontSize: '0.85rem', fontWeight: 700, color: B.text, marginBottom: '0.5rem' },
    expList: { fontSize: '0.8rem', color: B.muted, lineHeight: 1.65, paddingLeft: '1rem', margin: 0 },
    expNote: { fontSize: '0.78rem', color: B.faint, lineHeight: 1.6, marginTop: '0.75rem' },
  };

  const costPct = Math.min(100, (cost / 50) * 100);

  const disabledStyle = { opacity: 0.45, cursor: 'not-allowed' };

  return (
    <div style={s.wrap}>
      {/* ── Topbar ─────────────────────────────────────────────────────── */}
      <header style={s.topbar}>
        {onBack && (
          <button style={s.backBtn} onClick={onBack} aria-label="Back">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="10 3 5 8 10 13" />
            </svg>
            Back
          </button>
        )}
        <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
          <img src="/logo.svg" alt="brookebit" height={40} style={{ display: 'block' }} />
        </a>
        <h1 style={s.title}>Diffusion Denoising</h1>
        <span style={s.badge}>Case Study</span>
        <span style={s.lvlBadge}>SL · HL</span>
      </header>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <main style={s.body}>

        {/* ── Controls ───────────────────────────────────────────────── */}
        <div style={s.card}>
          <div style={s.sectionLabel}>Configuration</div>
          <div style={s.controlRow}>
            <div style={s.ctrlGroup}>
              <label style={s.ctrlLabel} htmlFor="target-picture">Target picture (training data)</label>
              <select
                id="target-picture"
                style={s.select}
                value={target}
                onChange={e => handleTargetChange(e.target.value)}
              >
                {TARGET_ORDER.map(k => (
                  <option key={k} value={k}>{TARGETS[k].label}</option>
                ))}
              </select>
            </div>

            <div style={s.ctrlGroup}>
              <label style={s.ctrlLabel} htmlFor="steps-range">Number of steps</label>
              <input
                id="steps-range"
                type="range" min="4" max="50" step="1"
                value={numSteps}
                onChange={e => handleStepsChange(+e.target.value)}
                style={{ accentColor: B.green, width: 160 }}
              />
              <span style={s.rangeHint}>{numSteps} steps · {numSteps <= 10 ? 'rougher' : numSteps >= 38 ? 'cleaner' : 'medium'} result</span>
            </div>

            <div style={{ marginLeft: 'auto', display: 'flex', gap: '1.4rem', alignItems: 'center' }}>
              <div style={s.stat}>
                <span style={{ ...s.statNum, color: B.greenDark }}>{step}<span style={{ color: B.faint, fontSize: '0.9rem' }}>/{numSteps}</span></span>
                <span style={s.statLabel}>Step</span>
              </div>
              <div style={{ width: 1, height: 34, background: B.border }} />
              <div style={s.stat}>
                <span style={s.statNum}>{cost}</span>
                <span style={s.statLabel}>Compute cost</span>
              </div>
            </div>
          </div>

          <div style={s.btnRow}>
            <button
              style={{ ...s.btnPrimary, ...(done ? disabledStyle : {}) }}
              onClick={handleStep}
              disabled={done}
              aria-label="Denoise one step"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="4 3 11 8 4 13" />
                <line x1="12.5" y1="3" x2="12.5" y2="13" />
              </svg>
              Denoise step
            </button>

            <button
              style={{ ...s.btnGhost, ...(reduceMotion || done ? disabledStyle : {}) }}
              onClick={handlePlayPause}
              disabled={reduceMotion || done}
              title={reduceMotion ? 'Auto-run is off because your system requests reduced motion. Use Denoise step.' : undefined}
              aria-label={playing ? 'Pause auto-run' : 'Play auto-run'}
            >
              {playing ? (
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                  <rect x="3.5" y="3" width="3.2" height="10" rx="1" />
                  <rect x="9.3" y="3" width="3.2" height="10" rx="1" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                  <polygon points="4,3 13,8 4,13" />
                </svg>
              )}
              {playing ? 'Pause' : 'Play'}
            </button>

            <button style={s.btnGhost} onClick={handleNewNoise} aria-label="Reset to fresh noise">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M13 8a5 5 0 1 1-1.5-3.5" />
                <polyline points="13 2 13 5 10 5" />
              </svg>
              New noise
            </button>
          </div>

          {/* Compute-cost bar (meaning is carried by the number + label, not colour) */}
          <div style={{ marginTop: '0.9rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: B.muted }}>
              <span>Compute cost: one CNN denoiser pass per step</span>
              <span style={{ fontFamily: mono }}>{cost} passes</span>
            </div>
            <div style={s.costTrack}>
              <div style={{
                width: `${costPct}%`, height: '100%',
                background: costPct > 70 ? B.amberDark : B.green,
                transition: reduceMotion ? 'none' : 'width 0.2s',
              }} />
            </div>
          </div>

          <p style={{ fontSize: '0.78rem', color: B.muted, marginTop: '0.85rem', lineHeight: 1.55 }}>
            <strong>Try it:</strong> the canvas starts as pure random <strong>noise</strong>. Press{' '}
            <strong>Denoise step</strong> (or Play) to remove a little noise each <strong>step</strong> and
            watch the picture emerge. More steps give a cleaner image but a higher compute cost. Change the{' '}
            <strong>target picture</strong> to see how the <strong>training data</strong> decides what appears.
          </p>
        </div>

        {/* ── Canvas ─────────────────────────────────────────────────── */}
        <div style={s.card}>
          <div style={s.sectionLabel}>
            {TARGETS[target].label} · step {step} of {numSteps}
            {step === 0 ? ' · pure noise' : done ? ' · denoising complete' : ' · denoising'}
          </div>
          <canvas
            ref={canvasRef}
            style={s.canvas}
            role="img"
            aria-label={
              step === 0
                ? 'A field of pure random noise. No picture is visible yet.'
                : `Partly denoised image of a ${TARGETS[target].label.toLowerCase()}: ${step} of ${numSteps} steps taken.`
            }
          />
        </div>

        {/* ── Explainer accordion ─────────────────────────────────────── */}
        <div style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
          <button style={s.expBtn} onClick={() => setHintOpen(o => !o)} aria-expanded={hintOpen}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke={B.green}
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 2h4M8 2v3M4.5 5.5A4 4 0 1 0 11.5 5.5" />
                <line x1="8" y1="9" x2="8" y2="12" />
                <circle cx="8" cy="13.5" r="0.5" fill={B.green} stroke="none" />
              </svg>
              How a diffusion model works
            </span>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke={B.green}
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
              style={{ transform: hintOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
              <polyline points="3 6 8 11 13 6" />
            </svg>
          </button>

          {hintOpen && (
            <div style={s.expBody}>
              <p style={{ fontSize: '0.85rem', color: B.text, lineHeight: 1.6, marginTop: '0.9rem' }}>
                A diffusion model builds an image by <strong>removing noise</strong>, not by painting.
                It begins with a full field of <strong>random noise</strong> (noise injection), never a
                photograph, and a neural <strong>denoiser</strong> takes away a little noise at each{' '}
                <strong>step</strong>. Repeat this over many steps and a picture appears.
              </p>

              <NoiseStrip />

              <p style={s.expNote}>
                The denoiser is a neural network, typically a{' '}
                <strong>convolutional neural network (CNN)</strong>. It has learned from{' '}
                <strong>training data</strong> what real images look like, so at each step it nudges the
                noisy field a little closer to a plausible image. The training data is what decides the
                content: train on foxes and foxes emerge; train on cityscapes and skylines emerge.
              </p>

              <div style={s.expGrid}>
                <div style={s.expCard}>
                  <div style={s.expCardTitle}>The key idea</div>
                  <ul style={s.expList}>
                    <li>Start from <strong>pure random noise</strong>, not a photo.</li>
                    <li>A <strong>CNN denoiser</strong> removes a little noise each step.</li>
                    <li>The <strong>training data</strong> decides what the image becomes.</li>
                  </ul>
                </div>
                <div style={s.expCard}>
                  <div style={s.expCardTitle}>Why it is costly</div>
                  <ul style={s.expList}>
                    <li>Each step runs the whole denoiser <strong>once</strong>.</li>
                    <li><strong>More steps</strong> give a cleaner image but more compute.</li>
                    <li>Generating one image can mean <strong>dozens of passes</strong> through a large network.</li>
                  </ul>
                </div>
              </div>

              <p style={s.expNote}>
                In this run so far the denoiser has been applied{' '}
                <strong style={{ fontFamily: mono }}>{cost}</strong> times. Slide the number of steps up
                for a cleaner result and watch the compute cost climb: the trade-off at the heart of why
                image generation is computationally demanding.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
